import { ChangeDetectorRef, Component, inject, LOCALE_ID, NgZone, OnInit } from '@angular/core';

import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthenticationService } from '../authentication/authentication.service';
import { FeatureSet, FeatureSetPlan } from '../models/server/FeatureSet';
import { FeatureService } from '../services/feature.service';
import { OrganisationService } from '../services/organisation.service';
import { RouterLink } from '@angular/router';

@Component({
    selector: 'app-home-pricing',
    templateUrl: './home-pricing.component.html',
    imports: [RouterLink]
})
export class HomePricingComponent implements OnInit {

  protected featureSets: FeatureSet[] = [];
  protected features: Map<string, Array<string>> = new Map<string, Array<string>>();
  protected xpFS: FeatureSet | undefined  = undefined;

  private countryCode: string = 'US';

  private locale = inject(LOCALE_ID);
  private organisationService = inject(OrganisationService);
  public authService = inject(AuthenticationService);
  private featureService = inject(FeatureService);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);

  ngOnInit(): void {
    // Location and feature sets are independent requests - run them in parallel rather
    // than chaining, since the pricing panels only need the country code for plan
    // selection (falls back to 'US', see getPlan()) and shouldn't wait on it. A slow or
    // failed location lookup also shouldn't block the pricing section from rendering at
    // all, so it falls back to the current countryCode instead of erroring the join (#1803).
    forkJoin({
      countryCode: this.organisationService.getLocation().pipe(catchError(() => of(this.countryCode))),
      featureSets: this.featureService.getFeatureSets()
    })
      .subscribe({
        next: ({ countryCode, featureSets: fs }) => {
          // Ensure updates run inside Angular's zone in case upstream observable emits outside it.
          this.ngZone.run(() => {
            this.countryCode = countryCode;
            this.xpFS = (fs || []).find(fsets => fsets.isExpansionPack);
            this.featureSets = (fs || []).filter(f => f.userSelectable);

            this.features = new Map<string, Array<string>>();
            for (const fset of this.featureSets) {
              const feet = this.getPlanFeatures(fset);
              if (feet != null) {
                this.features.set(fset.name, feet);
              }
            }

            // Force immediate refresh so control-flow blocks see latest state.
            this.cdr.detectChanges();
          });
        },
        error: () => {
        }
      });
  }

  handleRedirect(url: string, extraParameter: string) {
    window.location.href = url + extraParameter;
  }

  getPlan(fs: FeatureSet): FeatureSetPlan[] {
    let plan = fs.plans.find(p => p.countryCode === this.countryCode);
    if (plan == null) {
      plan = fs.plans.find(p => p.countryCode === 'US');
    }
    return plan ? [plan] : [];
  }

  private getPlanFeatures(fs: FeatureSet): Array<string> {
    const plans = this.getPlan(fs);
    if (plans.length) {
      if (plans[0] != null) {
        return this.featureService.genFeatureDescriptions(
          this.featureService.expandFeatureSet(fs, this.xpFS ?? fs, plans[0].expansionIncluded, 0), this.locale);
      }
    }
    return [];
  }

}
